<template>
  <div class="product-indications-fields">
    <div
      v-for="(item, index) in items"
      :key="item._key"
      class="product-indications-fields__item"
    >
      <div class="product-indications-fields__pair">
        <div class="product-indications-fields__field">
          <label class="form-label">{{ fieldLabel('shortRu') }}</label>
          <input
            v-model="item.short_text_ru"
            type="text"
            class="form-control"
            :class="{ 'is-readonly': isLocked }"
            :readonly="isLocked"
            :tabindex="isLocked ? -1 : undefined"
          />
        </div>
        <div class="product-indications-fields__field">
          <div class="product-indications-fields__label">
            <label class="form-label">{{ fieldLabel('fullRu') }}</label>
            <button
              v-if="canDelete(index)"
              type="button"
              class="btn btn-outline-danger btn-sm btn-icon-circle product-indications-fields__remove"
              :title="t('deleteIndication')"
              @click="onDelete(index)"
            >
              <i class="bi bi-trash" />
            </button>
          </div>
          <ClampTextField
            :model-value="item.full_text_ru"
            :locked="isLocked"
            @update:model-value="item.full_text_ru = $event"
          />
        </div>
      </div>
      <div class="product-indications-fields__pair">
        <div class="product-indications-fields__field">
          <label class="form-label">{{ fieldLabel('shortEn') }}</label>
          <input
            v-model="item.short_text_en"
            type="text"
            class="form-control"
            :class="{ 'is-readonly': isLocked }"
            :readonly="isLocked"
            :tabindex="isLocked ? -1 : undefined"
          />
        </div>
        <div class="product-indications-fields__field">
          <label class="form-label">{{ fieldLabel('fullEn') }}</label>
          <ClampTextField
            :model-value="item.full_text_en"
            :locked="isLocked"
            @update:model-value="item.full_text_en = $event"
          />
        </div>
      </div>
    </div>

    <div v-if="!isLocked">
      <button
        type="button"
        class="btn page-header-btn page-header-btn--ghost"
        @click="addRow"
      >
        <i class="bi bi-plus" aria-hidden="true" />
        {{ t('addIndication') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { v4 as uuid } from 'uuid'
import ClampTextField from '@/components/ClampTextField.vue'
import { useDeleteConfirm } from '@/composables/ui'
import { emptyIndication, serializeIndicationRows } from '@/lib/productCard'

/**
 * Показания Product List / прототип на стартовой: короткое и полное, RUS и ENG.
 * Полное поле раскрывается по клику и в просмотре, и в редактировании.
 */
const props = defineProps({
  /** Строки показаний. */
  modelValue: { type: Array, default: () => [] },
  /** Только просмотр (карточка ЛП). */
  readonly: { type: Boolean, default: false },
  /** Форма не в режиме редактирования. */
  disabled: { type: Boolean, default: false },
  /**
   * Набор подписей полей.
   * `prototype` — модалка Prototype of Indications на стартовой.
   */
  variant: {
    type: String,
    default: 'indication',
    validator: value => ['indication', 'prototype'].includes(value),
  },
})

const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()
const isLocked = computed(() => props.readonly || props.disabled)

/** Подпись поля с учётом variant (indication / prototype). */
function fieldLabel(key) {
  const prefix = props.variant === 'prototype' ? 'prototypeLabels' : 'labels'
  return t(`${prefix}.${key}`)
}

const items = computed({
  get: () => props.modelValue || [],
  set: value => emit('update:modelValue', value),
})

/** Первый блок остаётся всегда: единственную строку показаний не удаляют. */
function canDelete(index) {
  return !isLocked.value && index > 0
}

/** Добавляет пустую строку показания. */
function addRow() {
  items.value = [
    ...items.value,
    { ...emptyIndication(items.value.length + 1), _key: uuid() },
  ]
}

/**
 * Удаляет строку показания с подтверждением.
 *
 * @param {number} index Индекс строки.
 */
async function onDelete(index) {
  if (!canDelete(index)) {
    return
  }
  const active = document.activeElement
  if (active instanceof HTMLElement) {
    active.blur()
  }
  const scrollY = window.scrollY || document.scrollingElement?.scrollTop || 0
  const row = items.value[index]
  const text = String(
    row?.short_text_ru || row?.short_text_en || row?.full_text_ru || row?.full_text_en || ''
  ).trim()
  const subject = text ? t('deleteSubjectNamed', { text }) : t('deleteSubject')
  if (!(await useDeleteConfirm(subject))) {
    return
  }
  const next = items.value.slice()
  next.splice(index, 1)
  items.value = next.length
    ? next
    : [{ ...emptyIndication(1), _key: uuid() }]
  // Модалка подтверждения и снятие фокуса с кнопки иначе уводят карточку в начало.
  const keepScroll = () => window.scrollTo(0, scrollY)
  keepScroll()
  requestAnimationFrame(keepScroll)
  window.setTimeout(keepScroll, 400)
}

/**
 * Сериализация для PUT products (sync_indications).
 *
 * @returns {Array} Payload indications.
 */
function serialize() {
  return serializeIndicationRows(items.value)
}

defineExpose({
  serialize,
  resizeAll() {},
})
</script>

<i18n>
{
  "ru": {
    "addIndication": "Добавить показание",
    "deleteIndication": "Удалить показание",
    "deleteSubject": "показание",
    "deleteSubjectNamed": "показание {text}",
    "labels": {
      "shortRu": "Short indication RUS",
      "fullRu": "Full indication RUS",
      "shortEn": "Short indication ENG",
      "fullEn": "Full indication ENG"
    },
    "prototypeLabels": {
      "shortRu": "Short prototype of indications RUS",
      "fullRu": "Full prototype of indications RUS",
      "shortEn": "Short prototype of indications ENG",
      "fullEn": "Full prototype of indications ENG"
    }
  },
  "en": {
    "addIndication": "Add indication",
    "deleteIndication": "Delete indication",
    "deleteSubject": "indication",
    "deleteSubjectNamed": "indication {text}",
    "labels": {
      "shortRu": "Short indication RUS",
      "fullRu": "Full indication RUS",
      "shortEn": "Short indication ENG",
      "fullEn": "Full indication ENG"
    },
    "prototypeLabels": {
      "shortRu": "Short prototype of indications RUS",
      "fullRu": "Full prototype of indications RUS",
      "shortEn": "Short prototype of indications ENG",
      "fullEn": "Full prototype of indications ENG"
    }
  }
}
</i18n>

<style scoped>
.product-indications-fields {
  display: flex;
  flex-direction: column;
  gap: var(--ltms-field-stack-gap, 1.5rem);
  container-type: inline-size;
  container-name: indications;
}

.product-indications-fields__item {
  display: flex;
  flex-direction: column;
  gap: var(--ltms-field-stack-gap, 0.9rem);
}

/* Чуть больше воздуха между блоками, чтобы кнопка удаления не прилипала к полю сверху. */
.product-indications-fields__item + .product-indications-fields__item {
  margin-top: 0.5rem;
}

.product-indications-fields__pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--ltms-field-stack-gap, 0.9rem);
  min-width: 0;
}

.product-indications-fields__field {
  display: flex;
  flex-direction: column;
  /* Зазор подписи до поля — только margin .form-label (0.3rem), как у колонок карточки.
     Свой gap здесь складывался с ним и отрыв был вдвое больше. */
  gap: 0;
  min-width: 0;
}

.product-indications-fields__field > .form-label,
.product-indications-fields__label > .form-label {
  display: block;
}

/*
  Краткое и полное — два столбца по ширине карточки, не окна.
  Subgrid держит верх полей на одной линии, если подписи разной высоты.
*/
@container indications (min-width: 720px) {
  .product-indications-fields__pair {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    grid-template-rows: auto auto;
    column-gap: 1rem;
    /* 0: иначе row-gap subgrid добавляется к margin-bottom подписи. */
    row-gap: 0;
    align-items: start;
  }

  .product-indications-fields__field {
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span 2;
  }
}

/* Кнопка вне потока: иначе subgrid растягивает строку подписи и отрыв до поля растёт. */
.product-indications-fields__label {
  position: relative;
  min-width: 0;
}

.product-indications-fields__label > .form-label {
  padding-right: 2rem;
}

/* Чуть выше низа подписи: иначе круг визуально проседает относительно текста. */
.product-indications-fields__remove {
  position: absolute;
  top: auto;
  bottom: 3px;
  right: 0;
  z-index: 1;
}

.product-indications-fields__remove:hover:not(:disabled),
.product-indications-fields__remove:active:not(:disabled) {
  transform: none;
}
</style>
